import { ModalContent, ModalRenderProps, openModal } from "@decibeltrade/web-core/modal";
import { useTranslations } from "next-intl";
import { ReactNode } from "react";
import { css } from "styled-system/css";
import { stack } from "styled-system/patterns";

import { Analytics } from "@/lib/analytics";
import { linkStyles } from "@/recipes/link";

import { ModalCloseButton } from "./modal-close-button";

export const ENCRYPTION_MODAL_ID = "encryption-modal";

const ENCRYPTED_PENDING_TXNS_DOCS_URL =
  "https://aptos.dev/build/guides/encrypted-pending-transactions";

/** The two user-facing indicator states; `hidden` never opens the modal. */
export type EncryptionModalState = "paused" | "encrypted";

export interface EncryptionModalProps {
  state: EncryptionModalState;
}

export function openEncryptionModal(props: EncryptionModalProps, analytics: Analytics) {
  openModal({
    id: ENCRYPTION_MODAL_ID,
    renderContent: (modalProps) => <EncryptionModal {...modalProps} {...props} />,
  });
  analytics.ui.modalOpened({ modalName: ENCRYPTION_MODAL_ID });
}

function EncryptionModal({ state, close }: EncryptionModalProps & ModalRenderProps) {
  const t = useTranslations("encryption");
  const isPaused = state === "paused";

  return (
    <ModalContent className={stack({ w: "448", gap: "16" })}>
      <ModalCloseButton close={close} />

      <h2 className={css({ textStyle: "heading.xs", color: "text.primary", pr: "32" })}>
        {isPaused ? t("modalTitlePaused") : t("modalTitleEnabled")}
      </h2>

      <p className={descriptionStyles}>
        {t.rich("modalDescription", {
          link: (chunks: ReactNode) => (
            <a
              href={ENCRYPTED_PENDING_TXNS_DOCS_URL}
              target="_blank"
              rel="noreferrer"
              className={linkStyles}
            >
              {chunks}
            </a>
          ),
        })}
      </p>

      {isPaused && <p className={descriptionStyles}>{t("modalPausedDescription")}</p>}
    </ModalContent>
  );
}

const descriptionStyles = css({ textStyle: "body.md", color: "text.secondary" });
